import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import { CloudArrowUp, ListChecks } from '@phosphor-icons/react';
import { captureAnalytics } from '../../../actions/preload';
import { captureAnalyticsOncePerSession } from '../../../actions/helpers/sessionAnalytics';
import PacketUploadTooltip from './PacketUploadTooltip';
import { ClipboardPlus } from '../../../components/icons';
import { getChartStatusForTransmission } from './chartStatus';

export const CLICK_TO_VIEW_PATIENT_CHART = 'Click here to view Patient Chart';

const PATIENT_INFO_STATUS_KEYS = new Set([
  'packet_upload',
  'task_created',
  'task_after_chart_creation_success',
  'routed_to_sftp',
  'sftp_fallback_delivered',
  'sftp_category_in_progress',
  'sftp_category_retryable',
  'sftp_category_delivered',
  'sftp_category_failed',
]);

const iconStyle = {
  marginRight: 4,
};

const buildIconNode = (statusKey, hasLink, statusIconNode) => {
  if (statusKey === 'routed_to_sftp' || statusKey === 'sftp_fallback_delivered') {
    return <CloudArrowUp size={18} color="orange" style={iconStyle} />;
  }
  if (statusKey === 'packet_upload' || statusKey.startsWith('sftp_category_')) return statusIconNode;
  if (!hasLink) return null;

  switch (statusKey) {
    case 'task_created':
      return <ListChecks size={18} color="#52c41a" style={iconStyle} />;
    case 'task_after_chart_creation_success':
      return <ClipboardPlus size={18} color="#52c41a" style={iconStyle} />;
    default:
      return null;
  }
};

export const openPatientChartLink = (event, link) => {
  if (!link) return;

  window.open(link, '_blank', 'noopener,noreferrer');
  event.stopPropagation();
  event.preventDefault();
};

export const getPatientInfoStatusConfig = (transmission = {}, options = {}) => {
  const status = getChartStatusForTransmission(transmission, options);

  const isPacket = Boolean(transmission?.packet_upload?.parts?.length);
  if (!status || (!isPacket && !PATIENT_INFO_STATUS_KEYS.has(status.statusKey))) {
    return null;
  }

  let iconNode = buildIconNode(status.statusKey, Boolean(status.link), status.iconNode);
  if (!iconNode && isPacket) {
    // Match the ordinary inbox: Phosphor icons are 18px; Ant icons inherit text size.
    iconNode = React.cloneElement(status.iconNode, {
      ...(status.iconNode.props.size ? { size: 18 } : {}),
      style: { ...iconStyle, color: status.iconNode.props.style?.color },
    });
  }
  if (!iconNode) return null;

  return {
    ...status,
    iconNode,
  };
};

export const PatientInfoStatusIcon = ({ status }) => {
  if (!status || !status.iconNode) return null;

  const tooltipTitle = (
    <div>
      {status.parts ? <PacketUploadTooltip status={status} iconSize={18} /> : status.message}
      {status.link && status.parts && <div style={{ marginTop: 8 }}>{CLICK_TO_VIEW_PATIENT_CHART}</div>}
      {status.link && !status.parts && (
        <>
          <br />
          <br />
          {CLICK_TO_VIEW_PATIENT_CHART}
        </>
      )}
    </div>
  );

  const iconNode = status.link
    ? React.cloneElement(status.iconNode, {
      onClick: (event) => openPatientChartLink(event, status.link),
    })
    : status.iconNode;

  let trigger = iconNode;
  if (status.parts) {
    trigger = status.link ? (
      <a
        href={status.link}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${status.message}. ${CLICK_TO_VIEW_PATIENT_CHART}`}
        style={{ display: 'inline-flex' }}
        onClick={(event) => {
          event.stopPropagation();
          captureAnalytics('packet_upload_chart_opened', { surface: 'inbox' });
        }}
      >
        {status.iconNode}
      </a>
    ) : (
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      <span tabIndex={0} aria-label={status.message} style={{ display: 'inline-flex' }}>{status.iconNode}</span>
    );
  }

  return (
    <Tooltip
      title={tooltipTitle}
      placement={status.parts ? 'right' : 'top'}
      overlayStyle={status.parts ? { maxWidth: 380 } : undefined}
      trigger={['hover', 'focus']}
      onVisibleChange={(visible) => {
        if (visible && status.statusKey === 'packet_upload') {
          captureAnalyticsOncePerSession('packet_upload_status_viewed', { surface: 'inbox' });
        }
      }}
    >
      {trigger}
    </Tooltip>
  );
};

PatientInfoStatusIcon.defaultProps = {
  status: null,
};

PatientInfoStatusIcon.propTypes = {
  status: PropTypes.shape({
    message: PropTypes.string.isRequired,
    statusKey: PropTypes.string,
    parts: PropTypes.array,
    link: PropTypes.string,
    iconNode: PropTypes.node.isRequired,
  }),
};
